import {modalAnatomy} from '@chakra-ui/anatomy';
import {createMultiStyleConfigHelpers, defineStyle, defineStyleConfig} from '@chakra-ui/styled-system';
import {useMultiStyleConfig} from '@chakra-ui/system';
import {mode} from '@chakra-ui/theme-tools';

import {components as darkComponents} from '../../../design-tokens/dark';
import {components} from '../../../design-tokens/light';
import {core, typography} from '../../tokens';

const {defineMultiStyleConfig, definePartsStyle} = createMultiStyleConfigHelpers(modalAnatomy.keys);

const iconColorLight = components.colors.components.buttons.secondary[`icon-default`];
const iconColorDark = darkComponents.colors.components.buttons.secondary[`icon-default`];

const modalHeaderStyle = defineStyleConfig({
  baseStyle: {
    paddingTop: `space-300`,
    paddingInline: `space-300`,
    paddingBottom: `space-100`,
    ...typography.body.xl,
  },
});

const modalBodyStyle = defineStyleConfig({
  baseStyle: {
    paddingInline: `space-300`,
    paddingBottom: `space-300`,
  },
});

const modalFooterStyle = defineStyleConfig({
  baseStyle: {
    display: `flex`,
    alignItems: `center`,
    justifyContent: `flex-end`,
    paddingInline: `space-300`,
    paddingTop: `space-300`,
    paddingBottom: `space-300`,
    boxShadow: `0px 0px 14px 0px rgba(0, 0, 0, 0.03)`,
  },
});

const dialogStyle = defineStyleConfig({
  baseStyle: {
    boxShadow: core.shadows.modal,
    borderRadius: `radius-200`,
    bg: `white`,
    color: `gray.800`,
    width: `100%`,
    zIndex: `modal`,
    marginTop: `10vh`,
  },
  sizes: {
    full: {
      marginTop: `50px`,
      minHeight: `auto`,
      height: `calc(100% - 50px)`,
      borderTopRadius: `radius-300`,
    },
  },
});

const modalCloseButtonStyle = defineStyleConfig({
  baseStyle: defineStyle(props => ({
    top: `space-200`,
    right: `space-200`,
    color: mode(iconColorLight, iconColorDark)(props),
  })),
  sizes: {
    full: {
      right: `10px`,
      top: `-40px`,
      color: `text.inverse`,
    },
  },
});

const ModalTheme = defineMultiStyleConfig({
  baseStyle: definePartsStyle(props => {
    return {
      dialog: dialogStyle.baseStyle,
      closeButton: modalCloseButtonStyle.baseStyle?.(props),
      header: modalHeaderStyle.baseStyle,
      body: modalBodyStyle.baseStyle,
      footer: modalFooterStyle.baseStyle,
    };
  }),
  sizes: {
    sm: {},
    md: {},
    lg: {},
    xl: {},
    '2xl': {},
    full: {
      dialog: dialogStyle.sizes?.full,
      closeButton: modalCloseButtonStyle.sizes?.full,
    },
  },
  defaultProps: {
    size: `2xl`,
  },
});

export default ModalTheme;

export const useModalStyleConfig = <Part extends (typeof modalAnatomy.keys)[number]>(
  part: Part,
  props?: Parameters<typeof useMultiStyleConfig>[1],
) => {
  return useMultiStyleConfig(`Modal`, props ?? {})?.[part] as any;
};
